import { useEffect, useRef } from 'react';
import { hideAuthCTAModal, showAuthCTAModal } from 'containers/Auth/actions';
import _isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { makeSelectAuth, makeSelectProfile } from 'containers/Auth/selectors';
import { getLocalData } from 'utils/localDataStore';
import { useLocation } from 'react-router-dom';
import useIsAuthPath from 'components/Hooks/useIsAuthPath';
import qs from 'qs';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
  makeSelectOAuthOptions,
} from 'containers/Main/selectors';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import { hasClientAccessToFeature } from 'utils/clientSettings';
import {
  saveLoginSource,
  parseUtmParameters,
} from 'utils/sourceTrackingService';
import useIsEmailVerified from './useIsEmailVerified';

function useAuthenticationCTAModal() {
  const dispatch = useDispatch();
  const location = useLocation();
  const hasRun = useRef(false);
  const initialSearchObject = useRef(
    qs.parse(location.search, {
      ignoreQueryPrefix: true,
    }),
  );
  const auth = useSelector(makeSelectAuth());
  const profile = useSelector(makeSelectProfile());
  const authOptions = useSelector(makeSelectOAuthOptions());
  const isAuthPath = useIsAuthPath();
  const clientDetails = useSelector(makeSelectClientDetails());
  const { isEnabled, verified, requireVerification } = useIsEmailVerified();

  const [siteConfig] = useSiteConfigSelector(['Features']);
  const allowSignIn = useSelector(makeSelectAllowSignIn());
  const isBackModalEnabled =
    allowSignIn &&
    hasClientAccessToFeature({
      featuresSiteConfig: siteConfig,
      featureName: 'authenticationBackModal',
      clientDetails,
    });
  const isCTAModalEnabled =
    allowSignIn &&
    hasClientAccessToFeature({
      featuresSiteConfig: siteConfig,
      featureName: 'authenticationCallToActionModal',
      clientDetails,
    });

  const fromEmail = initialSearchObject.current.utm_source === 'email';

  const hasClientDetails = !_isEmpty(clientDetails);
  const disableHidingOfAuthModal =
    isEnabled && requireVerification && !verified;
  useEffect(() => {
    if (isBackModalEnabled && auth.isLoaded && auth.isEmpty) {
      const method = getLocalData('signMethod');

      if (hasClientDetails) {
        if (
          (method === 'facebook' && authOptions.facebook) ||
          (method === 'google' && authOptions.google) ||
          (method === 'microsoft.com' && authOptions.ms)
        ) {
          dispatch(
            showAuthCTAModal({
              type: 'welcome-back',
              method,
            }),
          );
        }
      } else if (
        method === 'facebook' ||
        method === 'google' ||
        method === 'microsoft.com'
      ) {
        dispatch(
          showAuthCTAModal({
            type: 'welcome-back',
            method,
          }),
        );
      }
    }
  }, [isBackModalEnabled, auth]);

  useEffect(() => {
    if (isCTAModalEnabled && !isAuthPath && auth.isLoaded && profile.isLoaded) {
      if (fromEmail) {
        if (!hasRun.current && auth.isEmpty && profile.isEmpty) {
          const userIdTemp = getLocalData('userIdTemp');
          if (userIdTemp) {
            const utmParams = parseUtmParameters();
            saveLoginSource(userIdTemp, utmParams, false);
          }

          dispatch(
            showAuthCTAModal({
              type: 'email',
              method: null,
            }),
          );
        }

        hasRun.current = true;
      } else if (auth.isEmpty && profile.isEmpty) {
        dispatch(
          showAuthCTAModal({
            type: 'default',
            method: null,
          }),
        );
      }
    }
  }, [isCTAModalEnabled, auth, fromEmail, isAuthPath, profile]);

  useEffect(() => {
    if (
      isCTAModalEnabled &&
      auth.isLoaded &&
      !auth.isEmpty &&
      !disableHidingOfAuthModal
    ) {
      dispatch(hideAuthCTAModal());
    }
  }, [isCTAModalEnabled, auth, fromEmail]);
}

export default useAuthenticationCTAModal;
